// context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  onAuthStateChanged, signInWithPopup, GoogleAuthProvider,
  signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { useSubscription } from '../stripe/useSubscription';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};

const buildDisplayName = (data, fallback) =>
  data?.nom?.nom
    ? `${data.nom.nom} ${data.nom.epellP || ''} ${data.nom.epellM || ''}`.trim()
    : fallback;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData,    setUserData]     = useState(null);
  const [authLoading, setAuthLoading]  = useState(true);

  const [language, setLanguage] = useState(() => {
    const l = navigator.language.split('-')[0].toLowerCase();
    return l === 'es' ? 'es' : 'en';
  });

  const { isActive, planKey, historicPlanKey, planName, planLimits, subscription, loading: subLoading } =
    useSubscription();

  useEffect(() => {
    let unsubscribeFirestore = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeFirestore) { unsubscribeFirestore(); unsubscribeFirestore = null; }

      if (user) {
        unsubscribeFirestore = onSnapshot(
          doc(db, 'users', user.uid),
          (snap) => {
            const data = snap.exists() ? snap.data() : null;
            setUserData(data);
            setCurrentUser((prev) => ({
              ...prev, ...user,
              displayName:   buildDisplayName(data, user.displayName),
              firestoreData: data,
            }));
            setAuthLoading(false);
          },
          (err) => {
            console.error('AuthContext Firestore error:', err);
            setUserData(null);
            setCurrentUser(user);
            setAuthLoading(false);
          }
        );
      } else {
        setCurrentUser(null);
        setUserData(null);
        setAuthLoading(false);
      }
    });

    const handleLangChange = () => {
      const l = navigator.language.split('-')[0].toLowerCase();
      setLanguage(l === 'es' ? 'es' : 'en');
    };
    window.addEventListener('languagechange', handleLangChange);

    return () => {
      unsubscribeAuth();
      unsubscribeFirestore?.();
      window.removeEventListener('languagechange', handleLangChange);
    };
  }, []);

  // useCallback: referencias estables → los consumidores no re-renderizan por cambio de función
  const loginWithGoogle   = useCallback(() => signInWithPopup(auth, new GoogleAuthProvider()), []);
  const loginWithEmail    = useCallback((e, p) => signInWithEmailAndPassword(auth, e, p), []);
  const registerWithEmail = useCallback((e, p) => createUserWithEmailAndPassword(auth, e, p), []);
  const logout            = useCallback(() => signOut(auth), []);

  const refreshUserData = useCallback(async () => {
    if (!currentUser) return;
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (snap.exists()) {
        const data = snap.data();
        setUserData(data);
        setCurrentUser((prev) => ({
          ...prev,
          displayName:   buildDisplayName(data, prev.displayName),
          firestoreData: data,
        }));
      }
    } catch (err) {
      console.error('refreshUserData error:', err);
    }
  }, [currentUser]);

  const loading = authLoading || subLoading;

  // useMemo: el objeto value no se recrea si sus dependencias no cambian
  const value = useMemo(() => ({
    currentUser, userData, loading, language, setLanguage,
    isActive, planKey, historicPlanKey, planName, planLimits, subscription,
    loginWithGoogle, loginWithEmail, registerWithEmail, logout, refreshUserData,
  }), [
    currentUser, userData, loading, language,
    isActive, planKey, historicPlanKey, planName, planLimits, subscription,
    loginWithGoogle, loginWithEmail, registerWithEmail, logout, refreshUserData,
  ]);

  // Renderizar children siempre, sin condicionar al estado de carga.
  //
  // Antes: {!loading && children}
  //   → El árbol de componentes NO se monta mientras Firebase auth + Stripe
  //     resuelven. App.jsx recibe null de useAuth() y no puede ni renderizar
  //     <LoadingScreen /> hasta que AuthProvider libere a sus hijos.
  //   → Cuando loading pasa a false, React monta todo el árbol desde cero
  //     (mount frío), lo que retrasa el primer paint útil.
  //
  // Ahora: {children}
  //   → El árbol se monta inmediatamente con loading=true.
  //   → App.jsx detecta loading=true y muestra <LoadingScreen /> (ya lo hacía).
  //   → Cuando loading pasa a false, es un re-render de componentes ya
  //     existentes (mount caliente), significativamente más rápido.
  //   → App.jsx es el único punto de control del estado de carga: sin duplicidad.
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};